/**
 * Auth-flow API helpers. Kept separate from `./api.ts` because the
 * login flow's "error" responses (401/423/429) are NOT exceptional —
 * they each map to a specific UI state and must be returned as data,
 * not thrown. The shared `api()` helper throws on every non-2xx, so
 * we use plain `fetch` here.
 */

export type LoginSuccess =
  | { ok: true; kind: 'mfa_required'; challenge_token: string }
  | { ok: true; kind: 'mfa_setup_required'; setup_token: string };

export type LoginFailure =
  | { ok: false; kind: 'invalid_credentials'; message: string }
  | { ok: false; kind: 'account_locked'; message: string; retryAfterSeconds: number | null }
  | { ok: false; kind: 'rate_limited'; message: string; retryAfterSeconds: number | null }
  | { ok: false; kind: 'network_error'; message: string };

export type LoginResult = LoginSuccess | LoginFailure;

export async function loginRequest(email: string, password: string): Promise<LoginResult> {
  let res: Response;
  try {
    res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (e) {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    };
  }

  const retryAfter = parseRetryAfter(res.headers.get('retry-after'));
  let payload: {
    status?: string;
    error?: string;
    message?: string;
    challenge_token?: string;
    setup_token?: string;
  } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    // body absent or not JSON — leave payload empty
  }

  if (res.status === 200 && payload.status === 'mfa_setup_required' && payload.setup_token) {
    return { ok: true, kind: 'mfa_setup_required', setup_token: payload.setup_token };
  }
  if (res.status === 200 && payload.status === 'mfa_required' && payload.challenge_token) {
    return { ok: true, kind: 'mfa_required', challenge_token: payload.challenge_token };
  }
  if (res.status === 423) {
    return {
      ok: false,
      kind: 'account_locked',
      message: payload.message ?? 'Compte temporairement verrouillé.',
      retryAfterSeconds: retryAfter,
    };
  }
  if (res.status === 429) {
    return {
      ok: false,
      kind: 'rate_limited',
      message: payload.message ?? 'Trop de tentatives. Réessayez dans une minute.',
      retryAfterSeconds: retryAfter,
    };
  }
  // 400 (zod), 401 (bad password), or any other non-success — bucket
  // them all into invalid_credentials. The API deliberately returns
  // identical messages for "no such user" and "wrong password" to
  // prevent email enumeration; we preserve that on the frontend.
  return {
    ok: false,
    kind: 'invalid_credentials',
    message: payload.message ?? 'Identifiants invalides.',
  };
}

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const n = Number(header);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

// Storage keys for the multi-step auth flow. Kept namespaced under
// crm.auth.* so they don't collide with other app state.
export const AUTH_STORAGE_KEYS = {
  challengeToken: 'crm.auth.challenge_token',
  setupToken: 'crm.auth.setup_token',
} as const;

// RFC 5322-lite. Frontend pre-flight only — the server's z.string().email()
// is the authoritative check.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(s: string): boolean {
  return EMAIL_RE.test(s.trim());
}

// ---------------------------------------------------------------------------
// MFA setup (first-login) flow
// ---------------------------------------------------------------------------

export type MfaSetupSuccess = {
  ok: true;
  qrCodeDataUrl: string;
  secretText: string;
  otpauthUri: string;
};

export type MfaSetupFailure =
  | { ok: false; kind: 'invalid_setup_token' }
  | { ok: false; kind: 'network_error'; message: string };

export type MfaSetupResult = MfaSetupSuccess | MfaSetupFailure;

export async function mfaSetupRequest(setupToken: string): Promise<MfaSetupResult> {
  let res: Response;
  try {
    res = await fetch('/api/v1/auth/mfa/setup', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Setup-Token': setupToken },
    });
  } catch {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    };
  }

  if (res.status === 401) return { ok: false, kind: 'invalid_setup_token' };

  let payload: { qr_code_data_url?: string; secret_text?: string; otpauth_uri?: string } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    // empty
  }
  if (!res.ok || !payload.qr_code_data_url || !payload.secret_text || !payload.otpauth_uri) {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Impossible de récupérer les données de configuration.',
    };
  }
  return {
    ok: true,
    qrCodeDataUrl: payload.qr_code_data_url,
    secretText: payload.secret_text,
    otpauthUri: payload.otpauth_uri,
  };
}

export type MfaActivateSuccess = {
  ok: true;
  recoveryCodes: string[];
  user: { id: string; email: string; role: string; display_name: string };
};

export type MfaActivateFailure =
  | { ok: false; kind: 'invalid_code' }
  | { ok: false; kind: 'invalid_setup_token' }
  | { ok: false; kind: 'network_error'; message: string };

export type MfaActivateResult = MfaActivateSuccess | MfaActivateFailure;

export async function mfaActivateRequest(
  setupToken: string,
  totpCode: string,
): Promise<MfaActivateResult> {
  let res: Response;
  try {
    res = await fetch('/api/v1/auth/mfa/activate', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Setup-Token': setupToken },
      body: JSON.stringify({ totp_code: totpCode }),
    });
  } catch {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    };
  }

  if (res.status === 401) return { ok: false, kind: 'invalid_setup_token' };
  if (res.status === 400) return { ok: false, kind: 'invalid_code' };

  let payload: {
    status?: string;
    recovery_codes?: string[];
    user?: { id: string; email: string; role: string; display_name: string };
  } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    // empty
  }
  if (!res.ok || !payload.recovery_codes || !payload.user) {
    return {
      ok: false,
      kind: 'network_error',
      message: "L'activation a échoué. Réessayez dans quelques instants.",
    };
  }
  return { ok: true, recoveryCodes: payload.recovery_codes, user: payload.user };
}

// ---------------------------------------------------------------------------
// MFA verify (returning-user TOTP step after password)
// ---------------------------------------------------------------------------

export type MfaVerifySuccess = {
  ok: true;
  user: { id: string; email: string; role: string; display_name: string };
};

export type MfaVerifyFailure =
  | { ok: false; kind: 'invalid_code' }
  | { ok: false; kind: 'challenge_expired' }
  | { ok: false; kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { ok: false; kind: 'network_error'; message: string };

export type MfaVerifyResult = MfaVerifySuccess | MfaVerifyFailure;

export async function mfaVerifyRequest(
  challengeToken: string,
  totpCode: string,
): Promise<MfaVerifyResult> {
  let res: Response;
  try {
    res = await fetch('/api/v1/auth/login/verify-mfa', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Challenge-Token': challengeToken },
      body: JSON.stringify({ totp_code: totpCode }),
    });
  } catch {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    };
  }

  if (res.status === 429) {
    return {
      ok: false,
      kind: 'rate_limited',
      retryAfterSeconds: parseRetryAfter(res.headers.get('retry-after')),
    };
  }

  let payload: {
    status?: string;
    error?: string;
    user?: { id: string; email: string; role: string; display_name: string };
  } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    // empty
  }

  // The API discriminates between an expired/invalid flow token
  // ('invalid_challenge_token') and a wrong TOTP digit
  // ('invalid_code') in the `error` field — both surface as 401.
  // Honour that distinction so the page can show the right copy.
  if (res.status === 401 && payload.error === 'invalid_challenge_token') {
    return { ok: false, kind: 'challenge_expired' };
  }
  if (res.status === 200 && payload.status === 'session_created' && payload.user) {
    return { ok: true, user: payload.user };
  }
  // 400 (zod), 401 invalid_code, 401 mfa_not_active, or anything else
  // odd — bucket as invalid_code so the user can retry. mfa_not_active
  // is conceptually distinct, but it only fires if the DB row was
  // manually altered between login and verify, which isn't a state
  // we surface a special copy for.
  return { ok: false, kind: 'invalid_code' };
}

// ---------------------------------------------------------------------------
// Password-reset request (anti-enumeration)
// ---------------------------------------------------------------------------

export type PasswordResetRequestResult =
  | { ok: true }
  | { ok: false; kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { ok: false; kind: 'server_error' }
  | { ok: false; kind: 'network_error'; message: string };

export async function passwordResetRequest(email: string): Promise<PasswordResetRequestResult> {
  let res: Response;
  try {
    res = await fetch('/api/v1/auth/password/reset-request', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    };
  }

  if (res.status === 200) return { ok: true };
  if (res.status === 429) {
    return {
      ok: false,
      kind: 'rate_limited',
      retryAfterSeconds: parseRetryAfter(res.headers.get('retry-after')),
    };
  }
  // 5xx and anything else odd — generic server error so the page
  // can surface a "try again later" message.
  return { ok: false, kind: 'server_error' };
}

// ---------------------------------------------------------------------------
// Password-reset confirm (token + new password + TOTP)
// ---------------------------------------------------------------------------

export type PasswordResetConfirmResult =
  | { ok: true }
  | { ok: false; kind: 'invalid_or_expired_token' }
  | { ok: false; kind: 'weak_password' }
  | { ok: false; kind: 'invalid_totp' }
  | { ok: false; kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { ok: false; kind: 'server_error' }
  | { ok: false; kind: 'network_error'; message: string };

export async function passwordResetConfirm(
  token: string,
  newPassword: string,
  totpCode: string,
): Promise<PasswordResetConfirmResult> {
  let res: Response;
  try {
    res = await fetch('/api/v1/auth/password/reset-confirm', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword, totp_code: totpCode }),
    });
  } catch {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    };
  }

  if (res.status === 429) {
    return {
      ok: false,
      kind: 'rate_limited',
      retryAfterSeconds: parseRetryAfter(res.headers.get('retry-after')),
    };
  }

  let payload: { ok?: boolean; error?: string } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    // empty
  }

  if (res.status === 200 && payload.ok) return { ok: true };

  if (res.status === 400) {
    if (payload.error === 'invalid_or_expired_token') {
      return { ok: false, kind: 'invalid_or_expired_token' };
    }
    if (payload.error === 'weak_password') return { ok: false, kind: 'weak_password' };
    if (payload.error === 'invalid_totp') return { ok: false, kind: 'invalid_totp' };
    // 'invalid_body' (zod failure) — we shouldn't hit this if the page
    // gates correctly, but if it does, treat it as a weak/invalid input
    // condition rather than crashing the UI. Bucket into weak_password
    // since it's the most actionable error in that situation.
    return { ok: false, kind: 'weak_password' };
  }
  return { ok: false, kind: 'server_error' };
}

// ---------------------------------------------------------------------------
// Recovery-code verify (alternate path if TOTP unavailable)
// ---------------------------------------------------------------------------

export type RecoveryCodeVerifySuccess = {
  ok: true;
  user: { id: string; email: string; role: string; display_name: string };
};

export type RecoveryCodeVerifyFailure =
  | { ok: false; kind: 'invalid_code' }
  | { ok: false; kind: 'challenge_expired' }
  | { ok: false; kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { ok: false; kind: 'network_error'; message: string };

export type RecoveryCodeVerifyResult = RecoveryCodeVerifySuccess | RecoveryCodeVerifyFailure;

export async function recoveryCodeVerifyRequest(
  challengeToken: string,
  recoveryCode: string,
): Promise<RecoveryCodeVerifyResult> {
  let res: Response;
  try {
    res = await fetch('/api/v1/auth/login/verify-recovery-code', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Challenge-Token': challengeToken },
      body: JSON.stringify({ recovery_code: recoveryCode }),
    });
  } catch {
    return {
      ok: false,
      kind: 'network_error',
      message: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    };
  }

  if (res.status === 429) {
    return {
      ok: false,
      kind: 'rate_limited',
      retryAfterSeconds: parseRetryAfter(res.headers.get('retry-after')),
    };
  }

  let payload: {
    status?: string;
    error?: string;
    user?: { id: string; email: string; role: string; display_name: string };
  } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    // empty
  }

  // Same discrimination as mfaVerifyRequest — backend collapses
  // "expired challenge token" and "wrong recovery code" into 401,
  // distinguished only by the `error` field.
  if (res.status === 401 && payload.error === 'invalid_challenge_token') {
    return { ok: false, kind: 'challenge_expired' };
  }
  if (res.status === 200 && payload.status === 'session_created' && payload.user) {
    return { ok: true, user: payload.user };
  }
  return { ok: false, kind: 'invalid_code' };
}
